import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { notification } from 'antd'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'
import { deleteMailTemplate, fetchMailTemplates, updateMailTemplateStatus } from '@/api/mail-templates.api'
import type { MailTemplate } from '@/types/api'
import { exportMailTemplates, useTableExport } from '@/api/dowload-excel.api'
import { buildColumns } from './columns'
import { MailTemplateFilterPanel } from './MailTemplateFilterPanel'
import type { MailTemplateFilterValues } from './MailTemplateFilterPanel'
import { MailTemplateDetailDrawer } from './MailTemplateDrawer'
import { MailTemplateFormDrawer } from './MailTemplateFormDrawer'

import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'
import { usePermission } from '@/hooks/use-permission'
import { formatEndDate, formatStartDate } from '@/utils/format'

const DEFAULT_FILTER: MailTemplateFilterValues = {
    name: '',
    title: '',
    functionId: undefined,
    unitId: '',
    status: undefined,
    time: undefined,
}

export function MailTemplatesPage() {
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 })

    const currentUser = useAtomValue(currentUserAtom)
    const showUnitColumn = currentUser?.userType === 1

    const canCreate = usePermission('mail-template-create')
    const canView = usePermission('mail-template-view')
    const canUpdate = usePermission('mail-template-update')
    const canDelete = usePermission('mail-template-delete')
    const canExport = usePermission('mail-template-export')

    const [showFilter, setShowFilter] = useState(false)
    const [filter, setFilter] = useState<MailTemplateFilterValues>(DEFAULT_FILTER)
    const [activeFilters, setActiveFilters] = useState<MailTemplateFilterValues>(DEFAULT_FILTER)
    const [isExporting, setIsExporting] = useState(false)
    const [viewingTemplate, setViewingTemplate] = useState<MailTemplate | null>(null)
    const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
    const [formMode, setFormMode] = useState<'create' | 'update'>('create')
    const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)
    const [deletingTemplate, setDeletingTemplate] = useState<MailTemplate | null>(null)
    const [togglingTemplate, setTogglingTemplate] = useState<MailTemplate | null>(null)

    const queryClient = useQueryClient()

    const filterParams = useMemo(() => {
        const fromDate = activeFilters.time?.from ? formatStartDate(activeFilters.time.from) : undefined
        const toDateRaw = activeFilters.time?.to || activeFilters.time?.from
        const toDate = toDateRaw ? formatEndDate(toDateRaw) : undefined

        return {
            page: pageIndex,
            limit: pageSize,
            name: activeFilters.name || undefined,
            title: activeFilters.title || undefined,
            functionId: activeFilters.functionId,
            unitId: showUnitColumn ? activeFilters.unitId : currentUser?.unitId,
            status: activeFilters.status,
            fromDate,
            toDate,
        }
    }, [pageIndex, pageSize, activeFilters, showUnitColumn, currentUser?.unitId])

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['mail-templates', filterParams],
        queryFn: ({ signal }) =>
            fetchMailTemplates(
                filterParams.page,
                filterParams.limit,
                {
                    name: filterParams.name,
                    title: filterParams.title,
                    functionId: filterParams.functionId,
                    unitId: filterParams.unitId,
                    status: filterParams.status,
                    fromDate: filterParams.fromDate,
                    toDate: filterParams.toDate,
                },
                signal,
            ),
        refetchOnMount: 'always',
        staleTime: 0,
        gcTime: 0,
    })
    const hasData = Boolean(data?.items && data.items.length > 0)

    const { mutate: toggleTemplateStatus, isPending: isToggling } = useMutation({
        mutationFn: (template: MailTemplate) => updateMailTemplateStatus(template, template.status === 1 ? 0 : 1),
        onSuccess: (response: any) => {
            notification.success({ message: response.responseMessage || 'Cập nhật trạng thái mẫu mail thành công!' })
            queryClient.invalidateQueries({ queryKey: ['mail-templates'] })
        },
        onSettled: () => setTogglingTemplate(null),
    })

    const { mutate: removeTemplate, isPending: isRemoving } = useMutation({
        mutationFn: (template: MailTemplate) => deleteMailTemplate(template.id),
        onSuccess: (response: any) => {
            notification.success({ message: response.responseMessage || 'Xóa mẫu mail thành công!' })
            queryClient.invalidateQueries({ queryKey: ['mail-templates'] })
        },
        onSettled: () => setDeletingTemplate(null),
    })

    const columns = useMemo(
        () =>
            buildColumns({
                pageIndex,
                pageSize,
                showUnitColumn,
                canView,
                canUpdate,
                canDelete,
                onViewDetail: (template) => setViewingTemplate(template),
                onUpdate: (template) => {
                    setFormMode('update')
                    setEditingTemplateId(template.id)
                    setIsFormDrawerOpen(true)
                },
                onDelete: (template) => setDeletingTemplate(template),
                onToggleStatus: (template) => setTogglingTemplate(template),
            }),
        [pageIndex, pageSize, showUnitColumn, canView, canUpdate, canDelete],
    )

    const rows = useMemo(() => data?.items ?? [], [data?.items])

    const { handleExport } = useTableExport<MailTemplate>(
        'Danh sách mẫu mail',
        columns,
        async (payload) => {
            try {
                setIsExporting(true)
                await exportMailTemplates(
                    {
                        name: filterParams.name,
                        title: filterParams.title,
                        functionId: filterParams.functionId,
                        unitId: filterParams.unitId,
                        status: filterParams.status,
                        fromDate: filterParams.fromDate,
                        toDate: filterParams.toDate,
                    },
                    payload,
                    'danh-sach-mau-mail.xlsx'
                )
            } finally {
                setIsExporting(false)
            }
        },
    )

    if (isLoading) {
        return (
            <section className="space-y-4">
                <PageHeader title="DANH SÁCH MẪU MAIL" isLoading />
                <div className="mt-6 text-center">Đang tải...</div>
            </section>
        )
    }

    return (
        <section className="space-y-4">
            <PageHeader
                title="DANH SÁCH MẪU MAIL"
                onToggleFilter={() => setShowFilter((v) => !v)}
                disabled={!hasData}
                onExport={canExport ? handleExport : undefined}
                isExporting={isExporting}
            >
                {canCreate && (
                    <Button
                        variant="primary"
                        size="md"
                        className="flex-1 gap-2 h-10"
                        onClick={() => {
                            setFormMode('create')
                            setEditingTemplateId(null)
                            setIsFormDrawerOpen(true)
                        }}
                    >
                        <Plus size={16} />
                        Thêm mới
                    </Button>
                )}
            </PageHeader>

            {showFilter && (
                <MailTemplateFilterPanel
                    values={filter}
                    onChange={setFilter}
                    onSearch={() => {
                        const trimmedFilter = {
                            ...filter,
                            name: filter.name.trim(),
                            title: filter.title.trim(),
                        }
                        setFilter(trimmedFilter)
                        setActiveFilters(trimmedFilter)
                        setPagination((s) => ({ ...s, pageIndex: 0 }))
                        refetch()
                    }}
                    showUnitColumn={showUnitColumn}
                />
            )}

            <DataTable
                columns={columns}
                data={rows}
                tableOptions={{
                    state: { pagination: { pageIndex, pageSize } },
                    onPaginationChange: setPagination,
                    getPaginationRowModel: getPaginationRowModel(),
                    getCoreRowModel: getCoreRowModel(),
                    manualPagination: true,
                    pageCount: data?.totalPages,
                }}
                footer={
                    <Pagination
                        page={pageIndex + 1}
                        pageCount={data?.totalPages ?? 1}
                        pageSize={pageSize}
                        totalCount={data?.total ?? 0}
                        onPageChange={(p) => setPagination((s) => ({ ...s, pageIndex: p - 1 }))}
                        onPageSizeChange={(size) => setPagination({ pageIndex: 0, pageSize: size })}
                    />
                }
            />

            {viewingTemplate && (
                <MailTemplateDetailDrawer
                    templateId={viewingTemplate.id}
                    showUnitColumn={showUnitColumn}
                    onClose={() => setViewingTemplate(null)}
                    onUpdate={(template) => {
                        setViewingTemplate(null)
                        setFormMode('update')
                        setEditingTemplateId(template.id)
                        setIsFormDrawerOpen(true)
                    }}
                />
            )}

            {isFormDrawerOpen && (
                <MailTemplateFormDrawer
                    isOpen={isFormDrawerOpen}
                    mode={formMode}
                    templateId={editingTemplateId}
                    onClose={() => {
                        setIsFormDrawerOpen(false)
                        setEditingTemplateId(null)
                    }}
                />
            )}

            <ConfirmModal
                isOpen={!!togglingTemplate}
                title="Xác nhận"
                description={<>Bạn chắc chắn muốn {togglingTemplate?.status === 1 ? 'vô hiệu hóa' : 'kích hoạt'} mẫu mail <strong>{togglingTemplate?.name}</strong> này?</>}
                variant="info"
                isLoading={isToggling}
                onClose={() => setTogglingTemplate(null)}
                onConfirm={() => togglingTemplate && toggleTemplateStatus(togglingTemplate)}
            />

            <ConfirmModal
                isOpen={!!deletingTemplate}
                title="Xóa mẫu mail"
                description={<>Bạn có chắc chắn muốn xóa mẫu mail <strong>{deletingTemplate?.name}</strong> này?</>}
                variant="danger"
                isLoading={isRemoving}
                onClose={() => setDeletingTemplate(null)}
                onConfirm={() => deletingTemplate && removeTemplate(deletingTemplate)}
            />
        </section>
    )
}